import React from 'react';
import styled, { keyframes } from 'styled-components';
import { testimonials } from '../data/testimonials';
import { Icon } from './Icon';

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(1.875rem);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const TestimonialsSection = styled.section`
  padding: 5rem 0;
  background-color: ${props => props.theme.colors.white};
`;

const Container = styled.div`
  max-width: 75rem;
  margin: 0 auto;
  padding: 0 2rem;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: 0 1rem;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
  animation: ${fadeInUp} 0.8s ease-out;
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.heading};
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  
  .text-primary {
    color: ${props => props.theme.colors.text};
  }
  
  .blue-gradient {
    background: ${props => props.theme.gradients.primary};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.desktop}) {
    font-size: 2.5rem;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    font-size: 2rem;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 1.75rem;
  }
`;

const SectionDescription = styled.p`
  font-size: 1.25rem;
  color: ${props => props.theme.colors.textLight};
  max-width: 48rem;
  margin: 0 auto;
  line-height: 1.6;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    font-size: 1.1rem;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 1rem;
  }
`;

const TestimonialsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(21.875rem, 1fr));
  gap: 2rem;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: repeat(auto-fit, minmax(18.75rem, 1fr));
    gap: 1.5rem;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const TestimonialCard = styled.div`
  background: ${props => props.theme.colors.secondary};
  border-radius: 1rem;
  padding: 2rem;
  position: relative;
  border: 0.0625rem solid ${props => props.theme.colors.lightGray};
  animation: ${fadeInUp} 0.6s ease-out;
  animation-fill-mode: both;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 1.5rem;
  }
`;

const QuoteIcon = styled.div`
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  opacity: 0.1;
  
  svg {
    width: 3rem;
    height: 3rem;
    color: ${props => props.theme.colors.primary};
  }
`;

const Rating = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  
  svg {
    width: 1.25rem;
    height: 1.25rem;
    color: var(--color-star);
    fill: currentColor;
  }
`;

const TestimonialText = styled.p`
  color: ${props => props.theme.colors.text};
  font-size: 1.125rem;
  line-height: 1.6;
  margin-bottom: 1.5rem;
  font-style: italic;
  
  &::before {
    content: '"';
  }
  
  &::after {
    content: '"';
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 1rem;
  }
`;

const TestimonialAuthor = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const AuthorAvatar = styled.div`
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  overflow: hidden;
  background: ${props => props.theme.colors.lightGray};
  display: flex;
  align-items: center;
  justify-content: center;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const AuthorInfo = styled.div``;

const AuthorName = styled.h4`
  color: ${props => props.theme.colors.text};
  font-weight: 700;
  font-size: 1.125rem;
  margin-bottom: 0.25rem;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 1rem;
  }
`;

const AuthorRole = styled.p`
  color: ${props => props.theme.colors.primary};
  font-size: 0.875rem;
  font-weight: 500;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 0.8rem;
  }
`;

export const Testimonials: React.FC = () => {
  return (
    <TestimonialsSection id="clientes">
      <Container>
        <SectionHeader>
          <SectionTitle>
            <span className="text-primary">Lo Que Dicen Nuestros </span>
            <span className="blue-gradient">Clientes</span>
          </SectionTitle>
          <SectionDescription>
            La confianza y satisfacción de nuestros clientes es nuestro mayor orgullo.
          </SectionDescription>
        </SectionHeader>

        <TestimonialsGrid>
          {testimonials.map((testimonial, index) => (
            <TestimonialCard 
              key={testimonial.name}
              style={{ animationDelay: `${index * 0.2}s` }}
            >
              <QuoteIcon>
                <Icon name="MessageCircle" size={20} />
              </QuoteIcon>

              <Rating>
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Icon key={i} name="Star" size={16} fill="currentColor" />
                ))}
              </Rating>

              <TestimonialText>
                {testimonial.comment}
              </TestimonialText>

              <TestimonialAuthor>
                <AuthorAvatar>
                  <img 
                    src="https://images.unsplash.com/photo-1550438655-400744b9fefc"
                    alt={testimonial.imageAlt}
                  />
                </AuthorAvatar>
                <AuthorInfo>
                  <AuthorName>{testimonial.name}</AuthorName>
                  <AuthorRole>{testimonial.role}</AuthorRole>
                </AuthorInfo>
              </TestimonialAuthor>
            </TestimonialCard>
          ))}
        </TestimonialsGrid>
      </Container>
    </TestimonialsSection>
  );
};
